/**
 * Mirrors the pre-signup analytics moments into our OWN database, alongside
 * the GA/Pixel dispatch that already happens.
 *
 * GA stays the authority for pre-signup RATES — how many people saw the
 * landing page, how many clicked. What GA cannot answer is "of the people who
 * signed up in July, which ones had played with the demo first", because GA's
 * identity does not join to a `users` row. These rows do: they are keyed
 * by `kv_did`, the same value `user_device_links` stores at signup.
 *
 * The list holds two KINDS of row. Most are FUNNEL NODES — cohort conditions a
 * later rate is computed against. `trial_file_rejected` and
 * `trial_upload_failed` are DIAGNOSTIC: nothing divides by them, and they exist
 * so a specific complaint ("I uploaded something and nothing happened") has an
 * answer that outlives the browser session it happened in.
 *
 * ## Why a whitelist here rather than edited call sites
 *
 * The call sites already fire exactly once per moment and already carry
 * the right parameters, and each has its own "report this once" latch. Editing
 * them would mean copying the excluded-path guard and the message-id minting
 * into every one of them, and the next event added would copy it again or
 * forget. One list, one guard.
 *
 * ## The excluded-path guard
 *
 * Capability URLs send nothing anywhere: GA and the Pixel are refused in
 * `track.ts`, and this module refuses the mirror with the same check. It is
 * read at DISPATCH time, from `window.location`: an event reported after an
 * `await` belongs to whatever page the visitor is on when the promise settles.
 */
import { reportHandledException } from '@/lib/error-reporting'
import type { AnonymousEventName } from '@kiao-voice/api-contract'
import { getOrCreateDeviceId } from '../identity/device-id'
import { getWebApiOrigin } from '../api'
import { webClientInfoHeaders } from '../web-client-info'
import { ANALYTICS_EVENTS, type AnalyticsEventName } from './events'
import { isOnAnalyticsExcludedPath } from './excluded-paths'
import type { AnalyticsParams } from './track'

/**
 * Per-event mirror config. `messageId` is only set for a moment that can
 * legitimately fire more than once for the SAME underlying thing (a page
 * refresh re-runs an effect); it derives a durable id from the device and the
 * event's own params instead of {@link newMessageId}, so the server's
 * `ON CONFLICT (message_id) DO NOTHING` is what turns the repeat into a no-op.
 * Every other event has no client-side latch problem — a genuinely new
 * interaction, and a fresh random id, is correct for those.
 */
interface MirroredEventConfig {
  messageId?: (deviceId: string, params: AnalyticsParams) => string
}

/**
 * The events mirrored to our API, and the ONLY names the server accepts on the
 * public ingest. Adding one is a deliberate edit in both places.
 */
const MIRRORED_EVENTS = new Map<string, MirroredEventConfig>([
  [ANALYTICS_EVENTS.heroDemoInteract, {}],
  [ANALYTICS_EVENTS.downloadAppClick, {}],
  [ANALYTICS_EVENTS.trialUploadStarted, {}],
  // The other half of the trial drop-off. `trial_upload_started` alone cannot
  // separate "left before the transcript finished" from "read it and did not
  // sign up", and those two want opposite fixes.
  [
    ANALYTICS_EVENTS.trialResultViewed,
    {
      // The call site's client-side "fire once per mount" ref resets on a hard
      // refresh of the result page, so a random id here would write a new row
      // every reload. Keying on the shortId (already public in the URL, so
      // this discloses nothing new) makes "this device viewed this result" the
      // dedup instead of "this call happened". A missing/blank short_id falls
      // back to a random id rather than becoming a SECOND durable key
      // (`...:trial_result_viewed:undefined`) that would silently collapse
      // every such call from one device onto a single row.
      messageId: (deviceId, params) => {
        const shortId = params.short_id
        if (typeof shortId !== 'string' || shortId.length === 0) return newMessageId()
        return `${deviceId}:trial_result_viewed:${shortId}`
      },
    },
  ],
  [ANALYTICS_EVENTS.campaignViewed, {}],
  // The two DIAGNOSTIC rows. They are not funnel nodes and nothing divides by
  // them — `funnel_query_service` does not read them. They are here because
  // support gets asked "I uploaded something and nothing happened", and the
  // only answer that survives is a row keyed by the same `kv_did` the rest of
  // this table uses: it says what this browser hit, and it joins to `users` if
  // that person later registered, which neither GA nor Sentry can do.
  [ANALYTICS_EVENTS.trialFileRejected, {}],
  // Deliberately a fresh random id per attempt (no `messageId` config): each
  // retry is its own failed attempt, and collapsing three onto one row would
  // hide that the visitor tried three times — the strongest signal there is.
  [ANALYTICS_EVENTS.trialUploadFailed, {}],
  // The third state of an attempt. `anonymous_trials` cannot stand in for it:
  // it has no `kv_did` to join back to this browser, and its rows are purged 24
  // hours after creation. One row per attempt, like the failure beside it.
  [ANALYTICS_EVENTS.trialUploadCompleted, {}],
])

export function isMirroredToServer(name: string): name is AnonymousEventName {
  return MIRRORED_EVENTS.has(name)
}

function newMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // jsdom and older Safari. Collisions only cost a dropped duplicate, and the
  // server's `ON CONFLICT DO NOTHING` is what makes that safe.
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
}

/** Drop `undefined` values so the JSON body matches the server's scalar contract. */
function scalarMetadata(params: AnalyticsParams): Record<string, string | number | boolean> {
  const clean: Record<string, string | number | boolean> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) clean[key] = value
  }
  return clean
}

/**
 * Send one mirrored moment. A no-op for anything off the list, during SSR,
 * without a device id, or on a capability URL.
 *
 * Fire-and-forget by design — an analytics write must never delay or break the
 * interaction it describes — and `keepalive` because two of the moments
 * (`download_app_click`, `trial_upload_started`) are immediately followed by a
 * navigation or a large upload that would otherwise cancel the request in
 * flight, silently losing exactly the events at the top of the funnel.
 */
export function mirrorEventToServer(name: AnalyticsEventName, params: AnalyticsParams = {}): void {
  if (typeof window === 'undefined') return
  if (!isMirroredToServer(name)) return
  if (isOnAnalyticsExcludedPath()) return

  const deviceId = getOrCreateDeviceId()
  if (!deviceId) return

  const config = MIRRORED_EVENTS.get(name)
  const messageId = config?.messageId ? config.messageId(deviceId, params) : newMessageId()

  const body = JSON.stringify({
    deviceId,
    name,
    occurredAt: new Date().toISOString(),
    messageId,
    metadata: scalarMetadata(params),
  })

  try {
    void fetch(`${getWebApiOrigin()}/api/v1/anonymous-events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...webClientInfoHeaders() },
      body,
      keepalive: true,
    }).catch((cause) => {
      reportHandledException(cause, { source: 'lib/analytics/server-mirror.ts' })
      console.warn('[analytics] server mirror failed:', cause)
    })
  } catch (cause) {
    reportHandledException(cause, { source: 'lib/analytics/server-mirror.ts' })
    // A synchronous throw (no fetch, a blocked request) must not reach the
    // click handler that reported the moment.
    console.warn('[analytics] server mirror could not be dispatched:', cause)
  }
}
